import PropTypes from 'prop-types';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

// Keep in sync with the .payments-tab-* mobile breakpoint in settings.css.
const MOBILE_BREAKPOINT = 767;
// Keep in sync with the .popover-backdrop opacity transition in settings.css.
const FADE_MS = 800;

/**
 * Generic popover anchored beneath a trigger element.
 *
 * The panel is rendered through a portal into `document.body`, so it is never
 * clipped by an ancestor that scrolls or hides overflow (e.g. the booking
 * detail tab container).
 *
 * - Desktop: `position: fixed`, anchored to the trigger's bounding rect and
 *   kept in sync on scroll/resize. Appears instantly.
 * - Mobile (<= MOBILE_BREAKPOINT): centered on screen behind a dim backdrop,
 *   so it reads as a modal. It fades in/out like the cancel booking dialog —
 *   the panel stays mounted for the fade-out before it is removed.
 */
const Popover = ({ open, onClose, trigger, children, align, testId }) => {
	const wrapperRef = useRef(null);
	const panelRef = useRef(null);
	const [position, setPosition] = useState(null);
	// `mounted` keeps the panel in the DOM through the mobile fade-out.
	const [mounted, setMounted] = useState(false);
	// `entered` drives the backdrop opacity (0 -> 1 in, 1 -> 0 out).
	const [entered, setEntered] = useState(false);

	useEffect(() => {
		if (open) {
			setMounted(true);
			return undefined;
		}
		if (!mounted) {
			return undefined;
		}
		// Mobile panel fades out before unmounting; desktop is removed at once.
		const delay =
			window.innerWidth <= MOBILE_BREAKPOINT ? FADE_MS : 0;
		const timer = setTimeout(() => {
			setMounted(false);
			setPosition(null);
		}, delay);
		return () => clearTimeout(timer);
	}, [open, mounted]);

	useLayoutEffect(() => {
		if (!mounted || !wrapperRef.current) {
			return undefined;
		}

		const updatePosition = () => {
			if (!wrapperRef.current) {
				return;
			}
			if (window.innerWidth <= MOBILE_BREAKPOINT) {
				setPosition({ centered: true });
				return;
			}
			const rect = wrapperRef.current.getBoundingClientRect();
			setPosition({
				centered: false,
				top: rect.bottom + 8,
				left: align === 'left' ? rect.left : null,
				right: align === 'left' ? null : window.innerWidth - rect.right
			});
		};

		updatePosition();
		// Capture phase so scrolling inside nested containers is caught too.
		window.addEventListener('scroll', updatePosition, true);
		window.addEventListener('resize', updatePosition);
		return () => {
			window.removeEventListener('scroll', updatePosition, true);
			window.removeEventListener('resize', updatePosition);
		};
	}, [mounted, align]);

	// Flip the opacity once the panel is mounted and positioned so the
	// transition has a frame to animate from.
	useEffect(() => {
		if (!open) {
			setEntered(false);
			return undefined;
		}
		if (!mounted || !position) {
			return undefined;
		}
		const timer = setTimeout(() => setEntered(true), 50);
		return () => clearTimeout(timer);
	}, [open, mounted, position]);

	useEffect(() => {
		if (!open) {
			return undefined;
		}

		const handlePointerDown = (event) => {
			const insideTrigger =
				wrapperRef.current && wrapperRef.current.contains(event.target);
			const insidePanel =
				panelRef.current && panelRef.current.contains(event.target);
			if (!insideTrigger && !insidePanel) {
				onClose();
			}
		};
		const handleKeyDown = (event) => {
			if (event.key === 'Escape') {
				onClose();
			}
		};

		document.addEventListener('mousedown', handlePointerDown);
		document.addEventListener('keydown', handleKeyDown);
		return () => {
			document.removeEventListener('mousedown', handlePointerDown);
			document.removeEventListener('keydown', handleKeyDown);
		};
	}, [open, onClose]);

	const renderPanel = () => {
		if (position.centered) {
			return (
				<div className="popover-backdrop" style={{ opacity: entered ? 1 : 0 }}>
					<div
						ref={panelRef}
						className="popover-panel popover-panel-centered"
						data-testid={testId}
					>
						{children}
					</div>
				</div>
			);
		}
		return (
			<div
				ref={panelRef}
				className="popover-panel"
				data-testid={testId}
				style={{
					top: position.top,
					left: position.left == null ? undefined : position.left,
					right: position.right == null ? undefined : position.right
				}}
			>
				{children}
			</div>
		);
	};

	return (
		<div className="popover-wrapper" ref={wrapperRef}>
			{trigger}
			{mounted && position && createPortal(renderPanel(), document.body)}
		</div>
	);
};

Popover.propTypes = {
	open: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	trigger: PropTypes.node.isRequired,
	children: PropTypes.node,
	align: PropTypes.oneOf(['left', 'right']),
	testId: PropTypes.string
};

Popover.defaultProps = {
	children: null,
	align: 'right',
	testId: undefined
};

export default Popover;
